import React from "react";

import { CONVERSION_SOURCE_INFERRED_AD_PLATFORM } from "@/api/insights";
import { Banner } from "@/design-system/components/Banner/Banner";
import { Typography } from "@/design-system/components/Typography/Typography";
import { EventSearchMultiSelect } from "@/InsightsPage/EventSearchMultiSelect";
import { masqueradeToken } from "@/util";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import { ConversionEventsStepProps } from "../types";
import { useSavedSelection } from "../useSavedSelection";
import {
  EventOptionsUnavailable,
  MissingEventHint,
} from "./EventOptionsStates";

export function LowFunnelStep({
  data,
  updateData,
  context,
  goNext,
}: ConversionEventsStepProps) {
  const {
    eventOptions,
    dynamicEventsData,
    pickerEvents,
    receivingEvents,
    retry,
  } = useSavedSelection({ data, updateData, context });
  const selected = data.bottomFunnelIds ?? [];
  const midFunnelIds = new Set(data.midFunnelIds ?? []);
  const inferred = (dynamicEventsData?.bottomFunnel ?? []).filter(
    (event) =>
      event.conversionSource === CONVERSION_SOURCE_INFERRED_AD_PLATFORM,
  );

  return (
    <WizardStepLayout
      title="Choose your main conversion"
      description="Pick the events where a visitor becomes a patient or customer, like “Appointment booked” or “Purchase”. Attribution credits your campaigns for these."
      primaryAction={{
        label: "Continue",
        onClick: goNext,
        disabled: selected.length === 0,
        disabledReason: "Choose at least one event",
      }}
    >
      {eventOptions.status !== "ready" ? (
        <EventOptionsUnavailable
          eventOptions={eventOptions}
          receivingEvents={receivingEvents}
          retry={retry}
        />
      ) : (
        <>
          {inferred.length > 0 && (
            <Banner type="info">
              Freshpaint picked {inferred.map((event) => event.name).join(", ")}{" "}
              from the conversions your ad platforms already receive. Keep them
              or choose your own.
              {!masqueradeToken() && " Saving confirms the ones you keep."}
            </Banner>
          )}
          <div className="flex flex-col gap-2">
            <Typography variant="label-sm">
              Low-funnel conversion events (up to 10)
            </Typography>
            <EventSearchMultiSelect
              eventDefs={pickerEvents.filter(
                (event) => !midFunnelIds.has(event.eventDefId),
              )}
              selectedEventDefIds={selected}
              onSelectionChange={(ids) => updateData({ bottomFunnelIds: ids })}
              placeholder="Search or select conversion events"
              maxSelected={10}
              popoverSide="bottom"
              expandOtherEventsWhenNoneRecommended
            />
            <Typography variant="body-xs" color="subtle">
              Only the first conversion in each session counts, so it’s fine to
              pick one event for each path through your funnel.
            </Typography>
          </div>
          <MissingEventHint
            receivingEvents={receivingEvents}
            hasUnprocessed={eventOptions.hasUnprocessed}
          />
        </>
      )}
    </WizardStepLayout>
  );
}
